import { Info, RefreshCw, SlidersHorizontal, CheckCircle2, XCircle } from "lucide-react";
import CustomAccordion from "app/uicomponents/microcomponents/CustomAccordion";
import { getSessionStore } from "app/utils/sessionStore";
import { THEME } from "app/utils/types";

const formatCurrency = (value: number): string =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);

const formatDate = (dateStr: string): string =>
  new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

// Replace with API data when endpoint is available
const MOCK_DATA = {
  grade: {
    letter: "A+",
    headline: "Modestly outperforming projections overall",
    subtext: "Supported by favourable loan usage and strong account value growth.",
  },
  align: {
    accountValue: 200000,
    accountValueDate: "2025-08-14",
    interestCredits: 15887,
    alignedValue: 215887,
  },
  index: {
    columns: [
      {
        main: { label: "ALIGN", value: 107.9, color: "#22C55E" },
        sub: { label: "ACCOUNT", value: 104.9, color: "#22C55E" },
      },
      {
        main: { label: "SURRENDER", value: 97.3, color: "#EF4444" },
        sub: { label: "BENEFIT", value: 104.9, color: "#22C55E" },
      },
      {
        main: { label: "PREMIUM", value: 104.9, color: "#1E3A8A" },
        sub: { label: "FINANCING", value: 104.9, color: "#22C55E" },
      },
    ],
  },
  status: {
    items: [
      {
        id: 1,
        title: "Agreement On File",
        description: "Is there a signed, written split dollar agreement in place?",
        pass: true,
      },
      {
        id: 2,
        title: "Loan Balance Visibility",
        description:
          "Is the current loan balance (including accrued interest, if any) known and documented?",
        pass: true,
      },
      {
        id: 3,
        title: "Premium Payment Match Agreement",
        description:
          "Are premiums being paid by the appropriate party and according to the contract?",
        pass: false,
      },
    ],
  },
};

// ── Shared helpers ─────────────────────────────────────────────────────────────

const hexPolygonPoints = (cx: number, cy: number, r: number): string =>
  Array.from({ length: 6 }, (_, i) => {
    const a = (Math.PI / 3) * i - Math.PI / 2; // pointy-top: first vertex at top
    return `${cx + r * Math.cos(a)},${cy + r * Math.sin(a)}`;
  }).join(" ");

// ── Sub-components ─────────────────────────────────────────────────────────────

const AccordionHeader = ({
  title,
  showInfo = true,
  isDark,
}: {
  title: string;
  showInfo?: boolean;
  isDark: boolean;
}) => (
  <div className="flex items-center gap-2">
    <span className={`text-[13px] font-semibold ${isDark ? "text-[#93C5FD]" : "text-[#1E3A8A]"}`}>
      {title}
    </span>
    {showInfo && <Info size={14} className="text-gray-400" />}
  </div>
);

/** Pointy-top hexagon badge with icon + value */
const HexBadge = ({
  label,
  value,
  color,
  large,
}: {
  label: string;
  value: number;
  color: string;
  large: boolean;
}) => {
  // Proper pointy-top proportions: width = height * √3/2
  const h = large ? 84 : 62;
  const w = Math.round(h * 0.866);
  const cx = w / 2;
  const cy = h / 2;
  const r = h / 2 - 3;

  return (
    <div className="flex flex-col items-center">
      <p
        className="text-[9px] font-bold uppercase tracking-widest mb-1.5"
        style={{ color, letterSpacing: "0.08em" }}
      >
        {label}
      </p>
      <div className="relative" style={{ width: w, height: h }}>
        <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="absolute inset-0">
          <polygon
            points={hexPolygonPoints(cx, cy, r)}
            fill="none"
            stroke={color}
            strokeWidth="2"
          />
        </svg>
        <div
          className="absolute inset-0 flex flex-col items-center justify-center"
          style={{ gap: 2 }}
        >
          <RefreshCw size={large ? 14 : 10} color={color} />
          <span
            className="font-bold leading-none"
            style={{ color, fontSize: large ? "13px" : "10px" }}
          >
            {value}
          </span>
        </div>
      </div>
    </div>
  );
};

/** Outline-only hexagon (control toggle in Index section) */
const OutlineHex = ({ isDark }: { isDark: boolean }) => {
  const size = 34;
  const cx = size / 2;
  const cy = size / 2;
  const r = cx - 2;
  const strokeColor = isDark ? "#6B7280" : "#CBD5E1";

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <polygon
        points={hexPolygonPoints(cx, cy, r)}
        fill="none"
        stroke={strokeColor}
        strokeWidth="1.5"
      />
    </svg>
  );
};

// ── Section contents ───────────────────────────────────────────────────────────

const TrueIqGradeContent = ({ isDark }: { isDark: boolean }) => {
  const descText = isDark ? "text-gray-300" : "text-[#334155]";
  const headlineText = isDark ? "text-gray-100" : "text-[#0F172A]";
  const subText = isDark ? "text-gray-400" : "text-[#64748B]";

  return (
    <div className="px-2 pb-4 pt-2">
      <p className={`text-[13px] leading-relaxed mb-6 ${descText}`}>
        <span className="font-semibold">TrueIQ Grade</span> is based on the performance of all key
        plan components—like account values and financed loans—each scored individually and weighted
        based on their importance. It&apos;s a fast, high-level snapshot of plan health.
      </p>
      <div className="flex items-center gap-6">
        <span className="text-[64px] font-bold text-[#3B82F6] leading-none tracking-tight">
          {MOCK_DATA.grade.letter}
        </span>
        <div>
          <p className={`text-[15px] font-semibold ${headlineText}`}>{MOCK_DATA.grade.headline}</p>
          <p className={`text-[13px] mt-1 ${subText}`}>{MOCK_DATA.grade.subtext}</p>
        </div>
      </div>
    </div>
  );
};

const TrueIqAlignContent = ({ isDark }: { isDark: boolean }) => {
  const descText = isDark ? "text-gray-300" : "text-[#334155]";
  const cardBg = isDark ? "bg-[#1E1E2E]" : "bg-[#F1F5F9]";
  const labelText = isDark ? "text-gray-400" : "text-[#94A3B8]";
  const valueText = isDark ? "text-gray-100" : "text-[#0F172A]";
  const dateText = isDark ? "text-gray-500" : "text-[#94A3B8]";
  const symbolText = isDark ? "text-gray-400" : "text-[#94A3B8]";

  return (
    <div className="px-2 pb-4 pt-2">
      <p className={`text-[13px] leading-relaxed mb-6 ${descText}`}>
        <span className="font-semibold">TrueIQ Align</span> enhances review accuracy by adjusting
        account (accumulated) values to reflect actual performance at the policy anniversary. This
        alignment helps displayed values more closely match year-end illustration projections.
      </p>
      <div className="flex items-center gap-4 flex-wrap">
        <div className={`rounded-xl p-4 min-w-[160px] ${cardBg}`}>
          <p className={`text-[10px] font-semibold uppercase tracking-wider mb-2 ${labelText}`}>
            Account Value
          </p>
          <p className={`text-[22px] font-bold ${valueText}`}>
            {formatCurrency(MOCK_DATA.align.accountValue)}
          </p>
          <p className={`text-[11px] mt-1 ${dateText}`}>
            {formatDate(MOCK_DATA.align.accountValueDate)}
          </p>
        </div>

        <span className={`text-[28px] font-light ${symbolText}`}>+</span>

        <div className={`rounded-xl p-4 min-w-[160px] ${cardBg}`}>
          <p className={`text-[10px] font-semibold uppercase tracking-wider mb-2 ${labelText}`}>
            Interest Credits
          </p>
          <p className={`text-[22px] font-bold ${valueText}`}>
            {formatCurrency(MOCK_DATA.align.interestCredits)}
          </p>
        </div>

        <span className={`text-[28px] font-light ${symbolText}`}>=</span>

        <div className={`rounded-xl p-4 min-w-[160px] ${cardBg}`}>
          <div className={`flex items-center gap-1.5 mb-2 ${labelText}`}>
            <RefreshCw size={11} />
            <p className="text-[10px] font-semibold uppercase tracking-wider">Aligned Value</p>
          </div>
          <p className={`text-[22px] font-bold ${valueText}`}>
            {formatCurrency(MOCK_DATA.align.alignedValue)}
          </p>
        </div>
      </div>
    </div>
  );
};

const TrueIqDisclosureContent = ({ isDark }: { isDark: boolean }) => {
  const bodyText = isDark ? "text-gray-300" : "text-[#334155]";

  return (
    <div className={`px-2 pb-4 pt-2 flex flex-col gap-3 text-[13px] leading-relaxed ${bodyText}`}>
      <p>
        TrueIQ is an optional, informational tool designed to support insurance and financial
        planning analysis. It is provided &quot;as is&quot; without warranty and is not intended to
        serve as a substitute for official carrier documents, in-force policy illustrations, legal
        agreements, or professional advice.
      </p>
      <p>
        All outputs generated by TrueIQ—including, but not limited to, Align values, Index scores,
        Plan Grades, Status scores, Protect metrics, and component-level analytics—are based on the
        data available or entered at the time of use. These outputs are inherently limited by data
        accuracy, user inputs, carrier reporting delays, market fluctuations, and assumptions made
        in the underlying methodologies. The resulting scores and grades are interpretive and
        indicative only; they do not represent a definitive assessment of policy performance, plan
        completeness, legal sufficiency, or financial adequacy.
      </p>
      <p>
        TrueIQ does not guarantee or warrant that any plan is properly funded, contractually
        enforceable, legally sound, compliant with applicable regulations, or appropriately
        structured for the intended planning objectives. Plan Grades, Indexes, or other scores
        should not be relied upon as a definitive measure of plan health, accuracy, sufficiency, or
        execution. No aspect of TrueIQ constitutes legal, tax, insurance, or investment advice, and
        users are expressly advised to consult with appropriate professionals prior to taking or
        recommending any action based on the platform&apos;s results.
      </p>
      <p>
        IAPP and its affiliates, employees, and partners disclaim all liability for any loss, error,
        inaccuracy, or outcome resulting from the use of TrueIQ or reliance upon its calculations.
        This includes, but is not limited to, misinterpretations, omissions, inaccurate inputs, or
        failure to act on third-party professional advice. By using TrueIQ, you acknowledge and
        accept full responsibility for the interpretation and application of its outputs and agree
        that IAPP assumes no liability for direct, indirect, incidental, consequential, or punitive
        damages arising from its use.
      </p>
    </div>
  );
};

const TrueIqIndexContent = ({ isDark }: { isDark: boolean }) => {
  const descText = isDark ? "text-gray-300" : "text-[#334155]";

  return (
    <div className="px-2 pb-4 pt-2">
      <p className={`text-[13px] leading-relaxed mb-6 ${descText}`}>
        <span className="font-semibold">TrueIQ Index</span> displays the ratio of actual values to
        illustrated values, helping quantify how results are tracking relative to expectations. A
        value above 100 indicates performance ahead of the original projection, while a value below
        100 reflects a shortfall. This index reflects the relationship between actual and projected
        values—it does not represent an annual return or interest credit.
      </p>

      {/* Hexagon grid */}
      <div>
        {/* Row 1: large hexes + controls */}
        <div className="flex items-start">
          <div className="flex items-start gap-10">
            {MOCK_DATA.index.columns.map((col) => (
              <HexBadge
                key={col.main.label}
                label={col.main.label}
                value={col.main.value}
                color={col.main.color}
                large
              />
            ))}
          </div>
          <div className="ml-auto flex items-center gap-2 self-center">
            <OutlineHex isDark={isDark} />
            <SlidersHorizontal size={20} className="text-gray-400" />
          </div>
        </div>

        {/* Row 2: small hexes, staggered */}
        <div className="flex items-start gap-10 mt-2" style={{ marginLeft: "38px" }}>
          {MOCK_DATA.index.columns.map((col) => (
            <HexBadge
              key={col.sub.label}
              label={col.sub.label}
              value={col.sub.value}
              color={col.sub.color}
              large={false}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const TrueIqStatusContent = ({ isDark }: { isDark: boolean }) => {
  const descText = isDark ? "text-gray-300" : "text-[#334155]";

  return (
    <div className="px-2 pb-4 pt-2">
      <p className={`text-[13px] leading-relaxed mb-6 ${descText}`}>
        <span className="font-semibold">TrueIQ Status</span> reflects the structural completeness of
        a plan—such as whether legal documents are in place, coverage is properly aligned, and
        planning elements have been kept up to date. This component is especially relevant for
        non-cash value strategies, where success depends on legal and strategic execution more than
        financial performance.
      </p>

      <div className="flex flex-col gap-3">
        {MOCK_DATA.status.items.map((item) => {
          const passBg = isDark
            ? "bg-[#14532D]/20 border-[#22C55E]/30"
            : "bg-[#F0FDF4] border-[#BBF7D0]";
          const failBg = isDark
            ? "bg-[#7F1D1D]/20 border-[#EF4444]/30"
            : "bg-[#FFF1F2] border-[#FECACA]";
          const titleColor = isDark ? "text-gray-100" : "text-[#0F172A]";
          const descColor = isDark ? "text-gray-400" : "text-[#64748B]";

          return (
            <div
              key={item.id}
              className={`flex items-start gap-4 rounded-xl border px-4 py-3.5 ${
                item.pass ? passBg : failBg
              }`}
            >
              <div className="flex-shrink-0 mt-0.5">
                {item.pass ? (
                  <CheckCircle2 size={22} className="text-[#22C55E]" />
                ) : (
                  <XCircle size={22} className="text-[#EF4444]" />
                )}
              </div>
              <div>
                <p className={`text-[13px] font-semibold ${titleColor}`}>{item.title}</p>
                <p className={`text-[12px] mt-0.5 ${descColor}`}>{item.description}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── Page ───────────────────────────────────────────────────────────────────────

function TrueIq() {
  const isDark = getSessionStore("globalTheme") === THEME.DARK;
  const cardBg = isDark ? "bg-[#2A2A3D]" : "bg-white";

  const sections = [
    {
      title: "TrueIQ Grade",
      content: <TrueIqGradeContent isDark={isDark} />,
      showInfo: true,
    },
    {
      title: "TrueIQ Align",
      content: <TrueIqAlignContent isDark={isDark} />,
      showInfo: true,
    },
    {
      title: "TrueIQ Index",
      content: <TrueIqIndexContent isDark={isDark} />,
      showInfo: true,
    },
    {
      title: "TrueIQ Status",
      content: <TrueIqStatusContent isDark={isDark} />,
      showInfo: true,
    },
    {
      title: "TrueIQ Disclosure",
      content: <TrueIqDisclosureContent isDark={isDark} />,
      showInfo: false,
    },
  ];

  return (
    <div className="flex flex-col gap-4 pb-6 mt-[180px]">
      {sections.map((s) => (
        <div key={s.title} className={`w-full rounded-2xl shadow-lg overflow-hidden ${cardBg}`}>
          <CustomAccordion
            header={<AccordionHeader title={s.title} showInfo={s.showInfo} isDark={isDark} />}
            content={s.content}
            isPendingProposal={false}
            activeIndex={0}
          />
        </div>
      ))}
    </div>
  );
}

export default TrueIq;
